import {
    GetDepartedVehiclesQueryParams,
    GetIncomingVehiclesQueryParams,
    GetInventoryVehiclesFilterQueryParams,
    GetInventoryVehiclesQueryParams,
    GetOutgoingVehiclesFilterQueryParams,
    GetOutgoingVehiclesQueryParams,
} from '../../compoundManagementApi';
import { mapToApiMeansOfTransportType } from '../meansOfTransportTypeMapper';
import { mapToApiVehicleType } from '../sharedVehicleMappers';
import {
    ApiDepartedVehicleSort,
    ApiIncomingVehicleSort,
    ApiInventoryVehicleSort,
    ApiOutgoingVehicleSort,
} from '../../types/compoundManagement/queryParameterApi.types';
import {
    DepartedVehicleQueryBody,
    IncomingVehicleQueryBody,
    InventorVehicleQueryBody,
    OutgoingVehicleQueryBody,
} from '../../types/compoundManagement/queryBody.types';
import { IncomingVehiclesTableColumn } from '../../../compoundManagement/incoming/table/IncomingVehiclesTableColumn';
import { SortDirection, TableSort } from '../../../sharedComponents/table/SortedTableHeader';
import { VehicleInventoryTableColumn } from '../../../compoundManagement/inventory/table/VehicleInventoryTableColumn';
import { OutgoingVehiclesTableColumn } from '../../../compoundManagement/outgoing/table/OutgoingVehiclesTableColumn';
import { DepartedVehiclesTableColumn } from '../../../compoundManagement/departed/table/DepartedVehiclesTableColumn';

const includeOnlyWhenPresent = <T extends object>(filter: T): T | undefined =>
    Object.values(filter).some((value) => value !== undefined) ? filter : undefined;

export const mapToIncomingVehiclesRequestBody = (
    queryParameters: GetIncomingVehiclesQueryParams,
): IncomingVehicleQueryBody => {
    const filter = mapToIncomingVehicleIdsRequestBody(queryParameters);
    return {
        filter: includeOnlyWhenPresent(filter),
        sort: queryParameters.sortBy
            ? mapToApiIncomingVehicleSort(queryParameters.sortBy)
            : ApiIncomingVehicleSort.EXPECTED_ARRIVAL_AT_ASC,
        limit: queryParameters.limit,
    };
};

export const mapToIncomingVehicleIdsRequestBody = (
    queryParameters: GetIncomingVehiclesQueryParams,
): NonNullable<IncomingVehicleQueryBody['filter']> => ({
    location: queryParameters.location,
    vehicle_type: queryParameters.vehicleType && mapToApiVehicleType(queryParameters.vehicleType),
    manufacturer: queryParameters.manufacturer,
    expected_arrival_date_start_at: queryParameters.expectedArrivalDateStartAt?.toISOString(),
    expected_arrival_date_end_at: queryParameters.expectedArrivalDateEndAt?.toISOString(),
    load_id: queryParameters.loadId,
    means_of_transport_id: queryParameters.meansOfTransportId,
    final_destination: queryParameters.finalDestination,
    vin_search: queryParameters.vins,
    exclude_cancelled: queryParameters.excludeCancelled ? true : undefined,
    arrival_from_location: queryParameters.arrivalFromLocation,
    arrival_means_of_transport_type: queryParameters.arrivalMeansOfTransportType
        ? mapToApiMeansOfTransportType(queryParameters.arrivalMeansOfTransportType)
        : undefined,
});

export const mapToInventoryVehiclesRequestBody = (
    queryParameters: GetInventoryVehiclesQueryParams,
): InventorVehicleQueryBody => {
    const filter = mapToInventoryVehicleIdsRequestBody(queryParameters);
    return {
        filter: includeOnlyWhenPresent(filter),
        sort: queryParameters.sortBy
            ? mapToApiInventoryVehicleSort(queryParameters.sortBy)
            : ApiInventoryVehicleSort.ARRIVAL_COMPLETED_AT_DESC,
        limit: queryParameters.limit,
    };
};

export const mapToInventoryVehicleIdsRequestBody = (
    queryParameters: GetInventoryVehiclesFilterQueryParams,
): NonNullable<InventorVehicleQueryBody['filter']> => ({
    location: queryParameters.location,
    vehicle_type: queryParameters.vehicleType && mapToApiVehicleType(queryParameters.vehicleType),
    manufacturer: queryParameters.manufacturer,
    arrival_date_start_at: queryParameters.arrivalDateStartAt?.toISOString(),
    arrival_date_end_at: queryParameters.arrivalDateEndAt?.toISOString(),
    final_destination: queryParameters.finalDestination,
    vin_search: queryParameters.vins,
    exclude_cancelled: queryParameters.excludeCancelled ? true : undefined,
    arrival_from_location: queryParameters.arrivalFromLocation,
    arrival_means_of_transport_type: queryParameters.arrivalMeansOfTransportType
        ? mapToApiMeansOfTransportType(queryParameters.arrivalMeansOfTransportType)
        : undefined,
});

export const mapToOutgoingVehiclesRequestBody = (
    queryParameters: GetOutgoingVehiclesQueryParams,
): OutgoingVehicleQueryBody => {
    const filter = mapToOutgoingVehicleIdsRequestBody(queryParameters);
    return {
        filter: includeOnlyWhenPresent(filter),
        sort: queryParameters.sortBy
            ? mapToApiOutgoingVehicleSort(queryParameters.sortBy)
            : ApiOutgoingVehicleSort.EXPECTED_DEPARTURE_AT_ASC,
        limit: queryParameters.limit,
    };
};

export const mapToOutgoingVehicleIdsRequestBody = (
    queryParameters: GetOutgoingVehiclesFilterQueryParams,
): NonNullable<OutgoingVehicleQueryBody['filter']> => ({
    location: queryParameters.location,
    vehicle_type: queryParameters.vehicleType && mapToApiVehicleType(queryParameters.vehicleType),
    manufacturer: queryParameters.manufacturer,
    expected_departure_date_start_at: queryParameters.expectedDepartureDateStartAt?.toISOString(),
    expected_departure_date_end_at: queryParameters.expectedDepartureDateEndAt?.toISOString(),
    load_id: queryParameters.loadId,
    means_of_transport_id: queryParameters.meansOfTransportId,
    final_destination: queryParameters.finalDestination,
    vin_search: queryParameters.vins,
    exclude_cancelled: queryParameters.excludeCancelled ? true : undefined,
    departure_to_location: queryParameters.departureToLocation,
    departure_means_of_transport_type: queryParameters.departureMeansOfTransportType
        ? mapToApiMeansOfTransportType(queryParameters.departureMeansOfTransportType)
        : undefined,
});

export const mapToDepartedVehiclesRequestBody = (
    queryParameters: GetDepartedVehiclesQueryParams,
): DepartedVehicleQueryBody => {
    const filter = {
        location: queryParameters.location,
        vehicle_type: queryParameters.vehicleType && mapToApiVehicleType(queryParameters.vehicleType),
        manufacturer: queryParameters.manufacturer,
        arrival_date_start_at: queryParameters.arrivalDateStartAt?.toISOString(),
        arrival_date_end_at: queryParameters.arrivalDateEndAt?.toISOString(),
        departure_date_start_at: queryParameters.departureDateStartAt?.toISOString(),
        departure_date_end_at: queryParameters.departureDateEndAt?.toISOString(),
        final_destination: queryParameters.finalDestination,
        vin_search: queryParameters.vins,
        exclude_cancelled: queryParameters.excludeCancelled ? true : undefined,
        arrival_from_location: queryParameters.arrivalFromLocation,
        arrival_means_of_transport_type: queryParameters.arrivalMeansOfTransportType
            ? mapToApiMeansOfTransportType(queryParameters.arrivalMeansOfTransportType)
            : undefined,
        departure_to_location: queryParameters.departureToLocation,
        departure_means_of_transport_type: queryParameters.departureMeansOfTransportType
            ? mapToApiMeansOfTransportType(queryParameters.departureMeansOfTransportType)
            : undefined,
    };

    return {
        filter: includeOnlyWhenPresent(filter),
        sort: queryParameters.sortBy
            ? mapToApiDepartedVehicleSort(queryParameters.sortBy)
            : ApiDepartedVehicleSort.DEPARTURE_COMPLETED_AT_DESC,
        limit: queryParameters.limit,
    };
};

export const mapToApiIncomingVehicleSort = (
    sorting: TableSort<IncomingVehiclesTableColumn>,
): ApiIncomingVehicleSort => {
    if (sorting.dir === SortDirection.ASCENDING) {
        switch (sorting.column) {
            case IncomingVehiclesTableColumn.EXPECTED_ARRIVAL_DATE:
                return ApiIncomingVehicleSort.EXPECTED_ARRIVAL_AT_ASC;
            case IncomingVehiclesTableColumn.LOCATION_ID:
                return ApiIncomingVehicleSort.LOCATION_IDENTIFIER_ASC;
            case IncomingVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiIncomingVehicleSort.FREIGHT_FORWARDER_ASC;
        }
    } else if (sorting.dir === SortDirection.DESCENDING) {
        switch (sorting.column) {
            case IncomingVehiclesTableColumn.EXPECTED_ARRIVAL_DATE:
                return ApiIncomingVehicleSort.EXPECTED_ARRIVAL_AT_DESC;
            case IncomingVehiclesTableColumn.LOCATION_ID:
                return ApiIncomingVehicleSort.LOCATION_IDENTIFIER_DESC;
            case IncomingVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiIncomingVehicleSort.FREIGHT_FORWARDER_DESC;
        }
    }
    throw new Error('Specified sort order is not implemented');
};

export const mapToApiInventoryVehicleSort = (
    sorting: TableSort<VehicleInventoryTableColumn>,
): ApiInventoryVehicleSort => {
    if (sorting.dir === SortDirection.ASCENDING) {
        switch (sorting.column) {
            case VehicleInventoryTableColumn.ARRIVAL_DATE:
                return ApiInventoryVehicleSort.ARRIVAL_COMPLETED_AT_ASC;
            case VehicleInventoryTableColumn.LOCATION_ID:
                return ApiInventoryVehicleSort.LOCATION_IDENTIFIER_ASC;
            case VehicleInventoryTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiInventoryVehicleSort.FREIGHT_FORWARDER_ASC;
        }
    } else if (sorting.dir === SortDirection.DESCENDING) {
        switch (sorting.column) {
            case VehicleInventoryTableColumn.ARRIVAL_DATE:
                return ApiInventoryVehicleSort.ARRIVAL_COMPLETED_AT_DESC;
            case VehicleInventoryTableColumn.LOCATION_ID:
                return ApiInventoryVehicleSort.LOCATION_IDENTIFIER_DESC;
            case VehicleInventoryTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiInventoryVehicleSort.FREIGHT_FORWARDER_DESC;
        }
    }
    throw new Error('Specified sort order is not implemented');
};

export const mapToApiOutgoingVehicleSort = (
    sorting: TableSort<OutgoingVehiclesTableColumn>,
): ApiOutgoingVehicleSort => {
    if (sorting.dir === SortDirection.ASCENDING) {
        switch (sorting.column) {
            case OutgoingVehiclesTableColumn.EXPECTED_DEPARTURE_DATE:
                return ApiOutgoingVehicleSort.EXPECTED_DEPARTURE_AT_ASC;
            case OutgoingVehiclesTableColumn.LOCATION_ID:
                return ApiOutgoingVehicleSort.LOCATION_IDENTIFIER_ASC;
            case OutgoingVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
                return ApiOutgoingVehicleSort.FREIGHT_FORWARDER_ASC;
        }
    } else if (sorting.dir === SortDirection.DESCENDING) {
        switch (sorting.column) {
            case OutgoingVehiclesTableColumn.EXPECTED_DEPARTURE_DATE:
                return ApiOutgoingVehicleSort.EXPECTED_DEPARTURE_AT_DESC;
            case OutgoingVehiclesTableColumn.LOCATION_ID:
                return ApiOutgoingVehicleSort.LOCATION_IDENTIFIER_DESC;
            case OutgoingVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
                return ApiOutgoingVehicleSort.FREIGHT_FORWARDER_DESC;
        }
    }
    throw new Error('Specified sort order is not implemented');
};

export const mapToApiDepartedVehicleSort = (
    sorting: TableSort<DepartedVehiclesTableColumn>,
): ApiDepartedVehicleSort => {
    if (sorting.dir === SortDirection.ASCENDING) {
        switch (sorting.column) {
            case DepartedVehiclesTableColumn.ARRIVAL_DATE:
                return ApiDepartedVehicleSort.ARRIVAL_COMPLETED_AT_ASC;
            case DepartedVehiclesTableColumn.DEPARTURE_DATE:
                return ApiDepartedVehicleSort.DEPARTURE_COMPLETED_AT_ASC;
            case DepartedVehiclesTableColumn.LOCATION_ID:
                return ApiDepartedVehicleSort.LOCATION_IDENTIFIER_ASC;
            case DepartedVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiDepartedVehicleSort.ARRIVAL_FREIGHT_FORWARDER_ASC;
            case DepartedVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
                return ApiDepartedVehicleSort.DEPARTURE_FREIGHT_FORWARDER_ASC;
        }
    } else if (sorting.dir === SortDirection.DESCENDING) {
        switch (sorting.column) {
            case DepartedVehiclesTableColumn.ARRIVAL_DATE:
                return ApiDepartedVehicleSort.ARRIVAL_COMPLETED_AT_DESC;
            case DepartedVehiclesTableColumn.DEPARTURE_DATE:
                return ApiDepartedVehicleSort.DEPARTURE_COMPLETED_AT_DESC;
            case DepartedVehiclesTableColumn.LOCATION_ID:
                return ApiDepartedVehicleSort.LOCATION_IDENTIFIER_DESC;
            case DepartedVehiclesTableColumn.ARRIVAL_FREIGHT_FORWARDER:
                return ApiDepartedVehicleSort.ARRIVAL_FREIGHT_FORWARDER_DESC;
            case DepartedVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
                return ApiDepartedVehicleSort.DEPARTURE_FREIGHT_FORWARDER_DESC;
        }
    }
    throw new Error('Specified sort order is not implemented');
};
